/**
 * Guards against office.js never becoming available.
 *
 * office.js is loaded by a plain <script> tag in index.html, from a CDN chosen at build time per
 * cloud (see the 'office-js-cdn' plugin in vite.config.ts). That request comes from the END USER's
 * browser or Outlook WebView, on the customer's network - hosting the add-in inside Azure US
 * Government says nothing about what their egress proxy permits. In GCC High / DoD, Microsoft's
 * published endpoint list has `*.cdn.office.net` as a REQUIRED host and does not list
 * `appsforoffice.microsoft.com` at all, so a tenant that hardens to deny-by-default blocks it.
 *
 * Left unguarded, that failure is completely silent:
 *   - `Office` is an *undeclared* identifier, not merely undefined, so even
 *     `typeof Office?.context?.mailbox` throws a ReferenceError - `typeof` only shields a bare
 *     identifier, never a member expression.
 *   - The bundle therefore dies at module scope, before APM is initialised, so there is no
 *     telemetry and no console error anyone will see.
 *   - #container keeps the empty `loader` class from index.html, whose `:empty` spinner rule in
 *     styles.scss is commented out - so the user gets a blank task pane.
 *
 * Blank pane, no error, no telemetry, on one customer's network only, is close to undiagnosable.
 * This module makes both shapes of the failure say what happened instead:
 *
 *   office.js never loaded            -> officeJsLoaded() is false, render at once
 *   office.js loaded, but the host
 *   never called Office.initialize    -> the watchdog fires after OFFICE_INIT_TIMEOUT_MS
 *
 * The rendering path here deliberately uses plain DOM and no React, i18n, or Fabric: a blocked CDN
 * is exactly the moment to depend on as little as possible.
 */

/**
 * How long to wait for the host to call Office.initialize before declaring the bootstrap failed.
 * Generous on purpose - a cold Outlook desktop WebView on a slow link is legitimately slow, and a
 * false positive here replaces a working add-in with an error screen.
 */
const OFFICE_INIT_TIMEOUT_MS = 20_000;

/** The office.js URL this page is actually loading, read back off the tag rather than re-derived. */
export const officeJsSrc = (): string =>
    document.getElementById('office-js')?.getAttribute('src') ?? '(no office-js script tag found)';

/**
 * The cloud this PAGE is running against, derived from the office.js host actually in the tag.
 *
 * Deliberately NOT a build-time constant (AB#14694), and there is no longer one to reach for.
 * The shipped image is built once with no cloud baked in; nginx rewrites the tag at container
 * start from the web app's OFFICE_CLOUD setting. So anything fixed at compile time would read
 * `commercial` on a gov web app whose page is correctly loading the GCC High CDN — a plausible
 * lie, in the telemetry you would reach for during an incident. Reading the host back makes the
 * value true by construction under either mechanism.
 *
 * When the host cannot be derived — no `office-js` tag at all, or an unrecognised host — this
 * returns `'unknown'` rather than guessing. The missing-tag case IS the `office-js-not-loaded`
 * failure being diagnosed, so it is exactly where a confident wrong answer does most harm.
 * `'unknown'` is a worse label to read and a better one to act on.
 */
export type OfficeCloud = 'commercial' | 'gcc' | 'gcch' | 'dod' | 'unknown';

export const officeCloud = (): OfficeCloud => {
    const src = officeJsSrc();
    if (src.includes('appsforoffice.gcch.cdn.office.net')) return 'gcch';
    if (src.includes('appsforoffice.dod.cdn.office.net')) return 'dod';
    if (src.includes('appsforoffice.gcc.cdn.office.net')) return 'gcc';
    if (src.includes('appsforoffice.microsoft.com')) return 'commercial';
    return 'unknown';
};

/**
 * Whether office.js actually loaded. Must be called before ANY other reference to `Office`,
 * including optional-chained ones - see the ReferenceError note above.
 */
export const officeJsLoaded = (): boolean => typeof Office !== 'undefined';

/**
 * `Office.context.mailbox` when office.js loaded and we're in a mailbox context, else undefined.
 * Use this instead of `Office?.context?.mailbox`, which throws when office.js is missing.
 */
export const officeMailbox = (): Office.Mailbox | undefined =>
    officeJsLoaded() ? Office.context?.mailbox : undefined;

let watchdog: ReturnType<typeof setTimeout> | undefined;

/**
 * Idempotent: clears any watchdog already running before starting a new one. Not currently
 * needed by the single call site in index.tsx, but this is a shared utility module, and a
 * second call without this would leak the first timer - it would keep running, orphaned from
 * `watchdog` (overwritten by the second call), and fire on its own original schedule even after
 * a later, unrelated cancelOfficeInitWatchdog() call.
 */
export const startOfficeInitWatchdog = (onTimeout: () => void): void => {
    cancelOfficeInitWatchdog();
    watchdog = setTimeout(onTimeout, OFFICE_INIT_TIMEOUT_MS);
};

export const cancelOfficeInitWatchdog = (): void => {
    if (watchdog !== undefined) {
        clearTimeout(watchdog);
        watchdog = undefined;
    }
};

export type OfficeBootstrapFailure = 'office-js-not-loaded' | 'office-initialize-never-fired';

/** One-line summary used for both the on-screen heading and the APM error message. */
export const officeBootstrapFailureMessage = (failure: OfficeBootstrapFailure): string =>
    failure === 'office-js-not-loaded'
        ? `office.js did not load from ${officeJsSrc()}`
        : `office.js loaded from ${officeJsSrc()} but Office.initialize was never called by the host`;

/**
 * Replaces the blank task pane with a diagnostic naming the exact URL that failed and the hosts
 * a tenant admin has to allow. The point is a support ticket that answers itself.
 */
export const renderOfficeBootstrapFailure = (failure: OfficeBootstrapFailure): void => {
    const container = document.getElementById('container');
    if (!container) return;

    // Drop `loader`, whose overflow:hidden would clip this, and stop React from being handed a
    // node we've written into directly.
    container.classList.remove('loader');
    container.textContent = '';

    const blocked = failure === 'office-js-not-loaded';

    const el = (tag: string, text?: string, style?: string): HTMLElement => {
        const node = document.createElement(tag);
        if (text) node.textContent = text;
        if (style) node.setAttribute('style', style);
        return node;
    };

    const wrap = el('div', undefined, 'padding:16px;font-family:"Segoe UI",system-ui,sans-serif;font-size:13px;line-height:1.5;color:#201f1e;');

    wrap.appendChild(el('h2', 'This add-in could not start', 'font-size:16px;margin:0 0 8px;'));

    wrap.appendChild(el(
        'p',
        blocked
            ? 'The Office JavaScript library could not be downloaded, so the add-in never received '
              + 'the connection to Outlook that it needs to run.'
            : 'The Office JavaScript library downloaded, but Outlook never finished initialising '
              + 'the add-in. Closing and reopening the task pane usually clears this.',
        'margin:0 0 12px;',
    ));

    if (blocked) {
        wrap.appendChild(el(
            'p',
            'This is almost always a network policy on this device or tenant blocking the address '
            + 'below. Ask your IT administrator to allow it, then reopen the task pane.',
            'margin:0 0 12px;',
        ));
    }

    // Read once and reused below for the host list: the number shown to the user and the
    // hosts they are told to allow must describe the same cloud.
    const cloud = officeCloud();

    // Monospaced and selectable: whoever raises the ticket needs to be able to copy these.
    const details = el('div', undefined, 'font-family:Consolas,monospace;font-size:12px;background:#f3f2f1;border:1px solid #edebe9;border-radius:2px;padding:8px;margin:0 0 12px;word-break:break-all;user-select:text;');
    details.appendChild(el('div', officeJsSrc()));
    details.appendChild(el('div', `cloud: ${cloud}    add-in version: ${__VERSION__}`, 'margin-top:6px;color:#605e5c;'));
    wrap.appendChild(details);

    if (blocked) {
        wrap.appendChild(el('p', 'Addresses this add-in requires:', 'margin:0 0 4px;font-weight:600;'));
        const hosts = el('ul', undefined, 'margin:0;padding-left:20px;');
        // Named per cloud rather than generically: in GCC High / DoD `*.cdn.office.net` is already
        // a required Microsoft 365 endpoint, so an admin seeing it listed can usually confirm the
        // block is elsewhere in minutes.
        // Three-way, because officeCloud() can be 'unknown' — the office-js tag is missing or
        // its host is unrecognised, which is common in exactly this failure. Naming one cloud's
        // hosts there would send an admin to check the wrong allowlist, so name both.
        const required = cloud === 'commercial'
            ? ['appsforoffice.microsoft.com', 'res.cdn.office.net']
            : cloud === 'unknown'
                ? ['appsforoffice.microsoft.com (commercial clouds)',
                   '*.cdn.office.net (GCC High / DoD — already a required Microsoft 365 endpoint there)']
                : ['*.cdn.office.net (a required Microsoft 365 endpoint in this cloud)'];
        required.forEach(host => hosts.appendChild(el('li', host)));
        hosts.appendChild(el('li', window.location.host));
        wrap.appendChild(hosts);
    }

    container.appendChild(wrap);
};
